import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRightIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { VehicleCard } from '../VehicleCard';
import { Reveal } from '../ui/Reveal';
import { useStore } from '../../contexts/StoreContext';
const MAX_FEATURED = 4;
const AUTOPLAY = 3500;
const ease = [0.23, 1, 0.32, 1] as const;

/** Carrusel en movimiento con los 4 0 km destacados; el auto activo se resalta. */
export function FeaturedCarousel() {
  const {
    vehicles
  } = useStore();
  const reduce = useReducedMotion();
  const featured = vehicles.filter(v => v.published && v.featured).slice(0, MAX_FEATURED);
  const trackRef = useRef<HTMLDivElement>(null);
  const programmaticUntil = useRef(0);
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  // Sincroniza el destacado cuando el usuario desliza a mano (celular).
  const onScroll = useCallback(() => {
    const el = trackRef.current;
    if (!el || Date.now() < programmaticUntil.current) return;
    const items = Array.from(el.children) as HTMLElement[];
    const left = el.scrollLeft;
    let idx = 0;
    items.forEach((item, i) => {
      if (Math.abs(item.offsetLeft - el.offsetLeft - left) < Math.abs(items[idx].offsetLeft - el.offsetLeft - left)) idx = i;
    });
    setActive(idx);
  }, []);
  const goTo = useCallback((i: number) => {
    const el = trackRef.current;
    const n = featured.length;
    if (!el || !n) return;
    const next = (i % n + n) % n;
    const item = el.children[next] as HTMLElement | undefined;
    if (!item) return;
    programmaticUntil.current = Date.now() + 700;
    el.scrollTo({
      left: item.offsetLeft - el.offsetLeft,
      behavior: 'smooth'
    });
    setActive(next);
  }, [featured.length]);

  // Avance automático; se pausa al pasar el cursor, tocar o enfocar.
  useEffect(() => {
    if (reduce || paused || featured.length < 2) return;
    const t = window.setTimeout(() => goTo(active + 1), AUTOPLAY);
    return () => window.clearTimeout(t);
  }, [active, paused, reduce, featured.length, goTo]);
  if (!featured.length) return null;
  const pause = () => setPaused(true);
  const resume = () => setPaused(false);
  return <section className="bg-navy/60 py-12" aria-labelledby="featured-title" aria-roledescription="carrusel" data-id="element-643" data-mp="3qqu1kws6iakb">
      <div className="container-x" data-id="element-644" data-mp="3lpwkyomthokx">
        <Reveal className="flex items-end justify-between gap-4" data-id="element-645" data-mp="3hpw3sw8c2za6">
          <div data-id="element-646" data-mp="3hss1cjg9rly3">
            <h2 id="featured-title" className="text-metal font-display text-3xl font-black tracking-tight sm:text-4xl" data-id="element-647" data-mp="3cr2oqzdef07w">0 KM DESTACADOS</h2>
            <p className="mt-1 text-sm text-mist" data-id="element-648" data-mp="37tjbo2sfsthj">Precio de contado, anticipo y cuota estimada de cada unidad.</p>
          </div>
          <div className="flex shrink-0 gap-2" data-id="element-649" data-mp="1axvbdvhcison">
            <button type="button" onClick={() => goTo(active - 1)} className="rounded-full border border-neon/50 bg-ink/80 p-2.5 transition-colors duration-150 hover:border-neon-hi" aria-label="Anterior" data-id="element-650" data-mp="160lddrfw58p7">
              <ChevronLeftIcon className="h-5 w-5" data-id="element-651" data-mp="1na78vhbv18mi" />
            </button>
            <button type="button" onClick={() => goTo(active + 1)} className="rounded-full border border-neon/50 bg-ink/80 p-2.5 transition-colors duration-150 hover:border-neon-hi" aria-label="Siguiente" data-id="element-652" data-mp="1nca54j3lvxzo">
              <ChevronRightIcon className="h-5 w-5" data-id="element-653" data-mp="zle9faw14in5" />
            </button>
          </div>
        </Reveal>

        <div ref={trackRef} onScroll={onScroll} onMouseEnter={pause} onMouseLeave={resume} onTouchStart={pause} onTouchEnd={resume} onFocus={pause} onBlur={resume} className="-mx-4 mt-4 flex snap-x snap-mandatory gap-5 overflow-x-auto px-4 py-4 [scrollbar-width:none] sm:mx-0 sm:px-1 [&::-webkit-scrollbar]:hidden" data-id="element-654" data-mp="14i4n5rkd325e">
          {featured.map((v, i) => {
          const on = i === active;
          return <motion.div key={v.id} className="relative w-[85%] shrink-0 snap-start rounded-2xl sm:w-[calc((100%-1.25rem)/2)] lg:w-[calc((100%-2.5rem)/3)]" role="group" aria-roledescription="destacado" aria-label={`${i + 1} de ${featured.length}`} animate={reduce ? undefined : {
            scale: on ? 1 : 0.95,
            opacity: on ? 1 : 0.6,
            boxShadow: on ? '0 0 0 2px #4D8CFF, 0 0 28px rgba(20,99,255,0.55)' : '0 0 0 0px rgba(77,140,255,0)'
          }} transition={{
            duration: 0.28,
            ease
          }} data-id="element-655" data-mp="2ejs72nhki4or" data-mpi={i}>
                <VehicleCard vehicle={v} data-id="element-656" data-mp="3g845aw4bktnr" />
              </motion.div>;
        })}
        </div>

        <div className="mt-4 flex flex-col items-center gap-5" data-id="element-657" data-mp="3ortcoap2b7z">
          <div className="flex gap-2" role="tablist" aria-label="Destacados" data-id="element-658" data-mp="7pk4xo71gs7f">
            {featured.map((v, i) => <button key={v.id} type="button" role="tab" aria-selected={i === active} aria-label={`Ir al destacado ${i + 1}`} onClick={() => goTo(i)} className={`relative h-2 overflow-hidden rounded-full transition-[width,background-color] duration-200 ease-out ${i === active ? 'w-10 bg-line' : 'w-2 bg-line hover:bg-mist'}`} data-id="element-659" data-mp="3ptlsqmpeblho" data-mpi={i}>
                {i === active && <motion.span key={`${active}-${paused}`} aria-hidden className="absolute inset-y-0 left-0 bg-neon-hi shadow-[0_0_10px_rgba(77,140,255,0.8)]" initial={{
              width: paused || reduce ? '100%' : '0%'
            }} animate={{
              width: '100%'
            }} transition={{
              duration: paused || reduce ? 0 : AUTOPLAY / 1000,
              ease: 'linear'
            }} data-id="element-660" data-mp="3c2gwlul8lbc3" />}
              </button>)}
          </div>
          <Link to="/catalogo" className="btn btn-metal group h-12 px-8" data-id="element-661" data-mp="29axp8t1i0jrj">
            VER EN EL CATÁLOGO
            <ArrowRightIcon className="h-4 w-4 transition-transform duration-150 group-hover:translate-x-1" aria-hidden data-id="element-662" data-mp="24de8nmhbvuwf" />
          </Link>
        </div>
      </div>
    </section>;
}